// ============================================
// Path: /frontend/src/pages/PropertiesPage.jsx
// Purpose: Properties landing page with search, categories, and featured agents
// UPDATED: Mobile-first responsive styling + sorting + search + rotation
// ============================================

// ========================================
// IMPORTS
// ========================================
import { useState, useEffect, useMemo } from 'react';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { propertyTypes } from '../lib/categories';
import { CategoryGridCard } from '../components/CategoryGridCard';
import { PropertiesSearchFilter } from '../components/PropertiesSearchFilter';
import { Building, Home, LandPlot, Star, Search, ArrowUpDown } from 'lucide-react';
import { ScrollArea, ScrollBar } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { AgentCard, AgentCardSkeleton } from '../components/AgentCard';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { usersAPI } from '../api/users.api';
import { CategoryToggle } from '../components/CategoryToggle';

// ========================================
// HELPER FUNCTIONS
// ========================================
const getPropertyIcon = (name) => {
  if (name.toLowerCase().includes('land')) {
    return <LandPlot className="w-5 h-5 sm:w-6 sm:h-6" />;
  }
  if (name.toLowerCase().includes('commercial')) {
    return <Building className="w-5 h-5 sm:w-6 sm:h-6" />;
  }
  if (name.toLowerCase().includes('godowns')) {
    return <Building className="w-5 h-5 sm:w-6 sm:h-6" />;
  }
  return <Home className="w-5 h-5 sm:w-6 sm:h-6" />;
};

// Fisher-Yates shuffle algorithm for random rotation
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// ========================================
// FEATURED AGENTS COMPONENT
// ========================================
const FeaturedAgents = () => {
  console.log('🎨 [FeaturedAgents] Component mounted');

  // ========================================
  // COMPONENT STATE
  // ========================================
  const [allAgents, setAllAgents] = useState([]);
  const [displayedAgents, setDisplayedAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState('random');
  const [rotationKey, setRotationKey] = useState(Date.now());

  // ========================================
  // DATA FETCHING
  // ========================================
  useEffect(() => {
    const fetchFeaturedAgents = async () => {
      console.log('🔵 [FeaturedAgents] Fetching featured agents...');
      setLoading(true);
      
      try {
        // Fetch ALL Gold and Platinum agents (no limit on fetch)
        const data = await usersAPI.getFeaturedAgents({
          subscriptionTier: 'GOLD,PLATINUM',
          limit: 1000 // Fetch all agents, we'll handle rotation client-side
        });
        console.log('✅ [FeaturedAgents] Featured agents received:', data);
        const fetchedAgents = data.users || [];

        // Filter out agents with no listings (same logic as RealEstateAgentsPage)
        const activeAgents = fetchedAgents.filter(agent => 
          agent._count && agent._count.listings > 0
        );
        console.log(`🔍 [FeaturedAgents] Filtered to ${activeAgents.length} agents with listings (from ${fetchedAgents.length} total)`);

        setAllAgents(activeAgents);
        
        // Set agents without initial shuffle - rotation will handle shuffling
        setDisplayedAgents(activeAgents.slice(0, 48));

        setError(null);
      } catch (err) {
        console.error('❌ [FeaturedAgents] Fetch error:', err);
        setError(err.response?.data?.message || 'Failed to load featured agents');
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedAgents();
  }, []);

  // ========================================
  // AUTO-ROTATION (Every 77 minutes)
  // ========================================
  useEffect(() => {
    if (allAgents.length === 0) return;

    const rotationInterval = 77 * 60 * 1000; // 77 minutes in milliseconds
    
    const intervalId = setInterval(() => {
      console.log('🔄 [FeaturedAgents] Auto-rotating agents (77 min interval)');
      const shuffled = shuffleArray(allAgents);
      setDisplayedAgents(shuffled.slice(0, 48));
      setRotationKey(Date.now()); // Force re-render
      
      // Reset to random sort when rotation happens
      if (sortOption !== 'random') {
        setSortOption('random');
      }
    }, rotationInterval);

    return () => clearInterval(intervalId);
  }, [allAgents, sortOption]);

  // ========================================
  // FILTERED AND SORTED AGENTS
  // ========================================
  const filteredAndSortedAgents = useMemo(() => {
    let filtered = displayedAgents;

    // Apply search filter
    if (searchQuery.trim()) {
      filtered = filtered.filter(agent => 
        agent.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        agent.email.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply sorting
    let sorted = [...filtered];
    switch (sortOption) {
      case 'name-asc':
        sorted.sort((a, b) => a.fullName.localeCompare(b.fullName));
        break;
      case 'name-desc':
        sorted.sort((a, b) => b.fullName.localeCompare(a.fullName));
        break;
      case 'random':
      default:
        // Already shuffled, keep as is
        break;
    }

    return sorted;
  }, [displayedAgents, searchQuery, sortOption, rotationKey]);

  // ========================================
  // RENDER
  // ========================================
  return (
    <section className="py-6 md:py-10">
      <h2 className="text-base sm:text-lg md:text-xl font-normal text-center mb-1 sm:mb-2 flex items-center justify-center gap-2">
        <Star className="w-4 h-4 sm:w-5 sm:h-5 text-amber-400" /> Featured Agents
      </h2>
      <p className="text-xs sm:text-sm md:text-base text-muted-foreground text-center mb-4 sm:mb-6">
        Connect with our top-tier Gold and Platinum real estate partners.
      </p>

      {/* Search and Sort Controls */}
      {!loading && allAgents.length > 0 && (
        <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row gap-3 sm:gap-4">
          {/* Search Input */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search agents by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 text-xs sm:text-sm h-9 sm:h-10"
            />
          </div>

          {/* Sort Dropdown */}
          <div className="w-full sm:w-48">
            <Select value={sortOption} onValueChange={setSortOption}>
              <SelectTrigger className="text-xs sm:text-sm h-9 sm:h-10">
                <ArrowUpDown className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="random" className="text-xs sm:text-sm">Random Order</SelectItem>
                <SelectItem value="name-asc" className="text-xs sm:text-sm">Name (A-Z)</SelectItem>
                <SelectItem value="name-desc" className="text-xs sm:text-sm">Name (Z-A)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {error && (
        <p className="text-destructive text-center mb-4 text-xs sm:text-sm">Error: {error}</p>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3 md:gap-4">
        {loading && Array.from({ length: 8 }).map((_, i) => (
          <AgentCardSkeleton key={i} />
        ))}
        
        {!loading && filteredAndSortedAgents.map((agent) => (
          <AgentCard 
            key={`${agent.id}-${rotationKey}`}
            agentId={agent.id}
            name={agent.fullName}
            email={agent.email}
          />
        ))}
      </div>

      {!loading && filteredAndSortedAgents.length === 0 && searchQuery && (
        <div className="text-center py-8 sm:py-12 text-muted-foreground">
          <Search className="w-8 h-8 sm:w-10 sm:h-10 mx-auto mb-2 sm:mb-3" />
          <p className="text-sm sm:text-base">No agents found matching "{searchQuery}"</p>
          <button 
            onClick={() => setSearchQuery('')}
            className="text-xs sm:text-sm text-primary hover:underline mt-2"
          >
            Clear search
          </button>
        </div>
      )}

      {!loading && allAgents.length === 0 && (
        <div className="text-center py-8 sm:py-12 text-muted-foreground">
          <p className="text-sm sm:text-base">No featured agents available at the moment.</p>
        </div>
      )}
    </section>
  );
};

// ========================================
// MAIN COMPONENT
// ========================================
export default function PropertiesPage() {
  console.log('🎨 [PropertiesPage] Component mounted');

  // ========================================
  // RENDER
  // ========================================
  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow container py-6 sm:py-8 md:py-12 px-4">
        <CategoryToggle />
        <PropertiesSearchFilter />

        <FeaturedAgents />
      </main>
      <Footer />
    </div>
  );
}